import React, {FC, createRef, useState, useEffect, useContext} from "react"

import Slider from "@irati/ui/src/Slider"
import Button from "@irati/ui/src/Button"
import Spacing from "@irati/ui/src/Spacing"
import wellnessService from "@irati/wellness/services/Wellness"
import {UserOnboarding, WellnessLane, WellnessMedia} from "@irati/types"
import tracking from "@irati-tracking/wellness"
import OnboardingModal, {OnboardingModalSteps} from "@irati/ui/src/OnboardingModal"
import onboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import authentication from "@irati/auth"
import {FormattedMessage, useIntl} from "@irati/utils/lang"
import History from "@irati/utils/history"
import WellnessBannersCarousel from "@irati/wellness/apollo/query/WellnessCarousel"
import {HeroSliderSettings} from "@irati/ui/src/Hero/styled"
import UsePerkboxAppBanner from "@irati-web/platform/src/components/UsePerkboxAppBanner"
import Loading from "@irati/ui/src/Loading"
import DrawerContext from "@irati/ui/src/AppContainer/Context/DrawerContext"
import {getDomain} from "@irati/utils/links"
import {triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import track from "@irati-tracking/platform/platform"
import {UTMMedium, UTMSource, addUTMToLink} from "@irati-web/platform/src/utils/shared"

import step1image from "../images/onboarding_01.svg"
import step2image from "../images/onboarding_02.svg"
import step3image from "../images/onboarding_03.svg"
import WellnessCategoryList from "../components/Category/WellnessCategoryList"
import WellnessLanes from "../components/WellnessLanes"
import HeroMedia from "../components/HeroMedia"

const onboardingModalRef = createRef()
const onboardingModalSettings: OnboardingModalSteps[] = [
  {
    image: step1image,
    title: "wellness.onboarding.step1.title",
    description: "wellness.onboarding.step1.description"
  },
  {
    image: step2image,
    title: "wellness.onboarding.step2.title",
    description: "wellness.onboarding.step2.description"
  },
  {
    image: step3image,
    title: "wellness.onboarding.step3.title",
    description: "wellness.onboarding.step3.description",
    actions: [
      <Button
        primary
        full
        onClick={() => {
          onboardingService.completeStep(authentication.user(), OnboardingStep.HAS_VIEWED_WELLNESS_ONBOARDING)
          onboardingModalRef.current.hideModal()
        }}>
        <FormattedMessage id="wellness.onboarding.finalStep.primaryCTA.text" />
      </Button>
    ]
  }
]

const WellnessHome: FC = () => {
  const [lanes, setLanes] = useState<WellnessLane[]>()
  const [premiumLane, setPremiumLane] = useState<WellnessLane | null>(null)
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false)
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const {setDrawerOpen} = useContext(DrawerContext)
  const {formatMessage} = useIntl()

  const {uuid = ""} = authentication.user()

  useEffect(() => {
    const promises = Promise.all([
      wellnessService.getLanes().then((response) => {
        tracking.viewWellnessHome()
        setLanes(response)
      }),
      onboardingService.get(uuid).then(({completedSteps}: UserOnboarding) => {
        setShowOnboarding(!completedSteps?.includes(OnboardingStep.HAS_VIEWED_WELLNESS_ONBOARDING))
      })
    ])

    setLoad(promises)

    wellnessService.wellnessMediaRentals().then((mediaRentals) => {
      if (mediaRentals?.results?.length === 0) {
        return
      }
      const premiumLaneObject = {
        ...mediaRentals,
        title: "Your Premium content",
        mediaCardCount: 3,
        mediaList: mediaRentals.results.map(({media}: (typeof mediaRentals.results)[number]) => media)
      }
      setPremiumLane(premiumLaneObject)
    })
  }, [])

  const selectCategory = (categoryId: string, categoryName: string) => {
    tracking.viewCategorySelected(categoryName)
    History.push({
      pathname: `/wellness/categories/${categoryId}`
    })
  }

  const handleCopyLink = (media: WellnessMedia) => {
    const drawerData = {
      url: addUTMToLink(
        `${getDomain(authentication.business().subdomain)}/wellness/details/${media.uuid}`,
        UTMSource.WELLNESS_CARD,
        UTMMedium.SHARE_LINK
      ),
      title: media.title,
      alertText: "platform.share.alertText",
      source: "wellness",
      type: "card",
      entity_id: media.uuid
    }
    if (navigator?.canShare?.(drawerData)) {
      setDrawerOpen(drawerData)
    } else {
      navigator.clipboard.writeText(drawerData.url).then(() => {
        track.share({source: "wellness", type: "card", entity_id: media.uuid})
        triggerSuccessToast(formatMessage({id: "wellness.copyVideoLink.title"}))
      })
    }
  }

  return (
    <Loading load={load}>
      <Spacing marginTop={4} />
      <UsePerkboxAppBanner text="platform.usePerkboxAppBanner.wellness" />
      <WellnessBannersCarousel>
        {(apollo) => {
          const carousel = apollo?.data?.getCarousel
          return (
            <Slider slidesToShow={1} settings={HeroSliderSettings}>
              {carousel &&
                carousel.map((banner: any) => (
                  <HeroMedia key={banner.id} onClick={() => tracking.clickBanner(banner.title)} media={banner} />
                ))}
            </Slider>
          )
        }}
      </WellnessBannersCarousel>

      <WellnessCategoryList showLink onClick={selectCategory} />
      <Spacing marginBottom={5} />
      {premiumLane && <WellnessLanes key="rentedMediaPremiumLane" lane={premiumLane} laneKey={0} onShowAlert={handleCopyLink} />}
      {lanes?.map((lane, laneKey: number) => (
        <WellnessLanes key={lane.uuid} lane={lane} laneKey={premiumLane ? laneKey + 1 : laneKey} onShowAlert={handleCopyLink} />
      ))}
      {showOnboarding && <OnboardingModal steps={onboardingModalSettings} ref={onboardingModalRef} />}
    </Loading>
  )
}

export default WellnessHome
